import cn from 'classnames';
import { useId } from 'react';

import { Icon, InputLabel } from '@/shared/ui';

import { useDismissableMenu } from '../../hooks';

import styles from './EditorSelect.module.scss';

type EditorSelectOption<Value extends string> = {
  value: Value;
  label: string;
  description?: string;
};

type EditorSelectProps<Value extends string> = {
  label: string;
  options: EditorSelectOption<Value>[];
  className?: string;
  dataComponent?: string;
  value?: Value;
  disabled?: boolean;
  onChange: (value: Value) => void;
};

export const EditorSelect = <Value extends string>(
  props: EditorSelectProps<Value>,
) => {
  const {
    label,
    options,
    className,
    dataComponent = 'template-editor-select',
    value,
    disabled,
    onChange,
  } = props;

  const labelId = useId();
  const triggerId = useId();

  const { containerRef, isOpen, setIsOpen } =
    useDismissableMenu<HTMLDivElement>();

  const current = options.find((option) => option.value === value);

  const handleSelect = (optionValue: Value) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-component={dataComponent}
    >
      <span id={labelId} className={styles.label}>
        <InputLabel label={label} />
      </span>
      <div ref={containerRef} className={styles.anchor}>
        <button
          id={triggerId}
          type="button"
          className={cn(styles.trigger, isOpen && styles.triggerOpen)}
          disabled={disabled}
          aria-haspopup="true"
          aria-labelledby={`${labelId} ${triggerId}`}
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <span className={styles.triggerLabel}>{current?.label ?? ''}</span>
          <Icon name="arrowDown" size={18} className={styles.arrow} />
        </button>
        {isOpen && (
          <div className={styles.menu}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(
                  styles.menuItem,
                  option.value === value && styles.menuItemActive,
                )}
                onClick={() => handleSelect(option.value)}
              >
                <span className={cn('p3', styles.menuItemLabel)}>
                  {option.label}
                </span>
                {option.description && (
                  <span className={cn('p4', styles.menuItemDescription)}>
                    {option.description}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
